import apiClient from './config';

export interface FamilyMember {
  id?: number;
  name: string;
  relationship: string;
  birthdate: string;
}

export interface QuestionnaireData {
  gender: string;
  ageRange: string;
  dateOfBirth: string;
  relationshipStatus: string;
  personalityType: string;
  lifePrincipleInspirations: string;
  hasChildren: boolean;
  numberOfChildren: number;
  childrenBirthdates: string[];
  familyMembers: FamilyMember[];
  hobbies: string[];
  relaxationActivities: string[];
  interestTopics: string[];
  engagementMethod: string;
  favouriteBooks: string[];
  favouriteAuthors: string[];
  booksOrSummaries: string;
  contentPreferences: string[];
  readingPreference: string;
  bookTitle: string;
  authorName: string;
}

export interface QuestionnaireStatusResponse {
  completed: boolean;
  count: number;
  lastCreatedAt: string | null;
}

export const questionnaireAPI = {
  submitQuestionnaire: async (data: QuestionnaireData) => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id || user.userId;
    const companyId = user.companyId;

    const payload = {
      userId,
      companyId,
      ...data
    };

    const response = await apiClient.post('/createStartUpQuestions', payload);
    return response.data;
  },

  getQuestionnaireStatus: async (): Promise<QuestionnaireStatusResponse> => {
    const response = await apiClient.get('/startup-questions/status');
    return response.data.data;
  },

  getTodaysRecommendations: async () => {
    const response = await apiClient.get('/recommendations/today');
    return response.data;
  },

  getFamilyMembers: async (): Promise<FamilyMember[]> => {
    const response = await apiClient.get('/family-members');
    return response.data.data || [];
  },

  addFamilyMember: async (data: { name: string; relationship: string; birthdate: string }) => {
    const response = await apiClient.post('/family-members', data);
    return response.data;
  },

  updateFamilyMember: async (id: number, data: { name: string; relationship: string; birthdate: string }) => {
    const response = await apiClient.put(`/family-members/${id}`, data);
    return response.data;
  },

  deleteFamilyMember: async (id: number) => {
    const response = await apiClient.delete(`/family-members/${id}`);
    return response.data;
  },

  getQuestionnaireById: async (id: number) => {
    const response = await apiClient.get(`/admin/questionnaires/${id}`);
    return response.data;
  },

  createQuestionnaire: async (data: Record<string, unknown>) => {
    const response = await apiClient.post('/admin/questionnaires', data);
    return response.data;
  },

  updateQuestionnaire: async (id: number, data: Record<string, unknown>) => {
    const response = await apiClient.put(`/admin/questionnaires/${id}`, data);
    return response.data;
  },

  // ----- Builder helpers (PR #295) -----
  getDynamicQuestions: async (questionnaireId: number) => {
    const response = await apiClient.get(`/companyAdmin/getDynamicQuestions/${questionnaireId}`);
    return response.data;
  },

  createDynamicQuestion: async (data: Record<string, unknown>) => {
    const response = await apiClient.post('/companyAdmin/createDynamicQuestion', data);
    return response.data;
  },

  deleteDynamicQuestion: async (questionId: number) => {
    // Backend expects body {questionId} on a DELETE — axios allows it as `data`.
    const response = await apiClient.delete('/companyAdmin/deleteDynamicQuestion', { data: { questionId } });
    return response.data;
  },

  updateDynamicQuestion: async (id: number, data: Record<string, unknown>) => {
    const response = await apiClient.put(`/companyAdmin/updateDynamicQuestion/${id}`, data);
    return response.data;
  },

  reorderDynamicQuestions: async (questionnaireId: number, order: Array<{ id: number; orderIndex: number }>) => {
    const response = await apiClient.post('/companyAdmin/reorderDynamicQuestions', { questionnaireId, order });
    return response.data;
  },

  getQuestionnaireResponses: async (questionnaireId: number) => {
    const response = await apiClient.get(`/companyAdmin/questionnaireResponses/${questionnaireId}`);
    return response.data;
  }
};
